import React from 'react';
import ResolveControlRenderer from './ResolveControlRenderer';
import { defaultValueOf } from '../manifest/valueUtils';
import { ManifestSetting } from '../manifest/types';

interface ResolveCompositeRowProps {
  setting: ManifestSetting;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  /** `resolve-config-row-<name>-control` — the outer container's own testid; each child
   * renders at `${testId}-${child.name}`. */
  testId: string;
}

/**
 * Generic renderer for `control: "composite"` (`widget_frame`, `tappable_options`,
 * `keyword_meaning_balance`, `result_counts`, `type_ahead`, `price_range_limits`,
 * `budget_split`) — loops over `setting.children`, dispatching each through
 * `ResolveControlRenderer`, in the same flex-wrap layout `widget_frame` used by hand
 * before this existed. A composite with no children (`budget_split`) renders just the
 * empty container, matching that setting's previous placeholder markup exactly.
 *
 * A child's value comes from the composite's own stored value object when present, else
 * the child's own manifest default — a composite override may itself be partial (only
 * some children ever saved), same reasoning as `valueUtils.ts::effectiveValueOf`.
 */
const ResolveCompositeRow: React.FC<ResolveCompositeRowProps> = ({ setting, value, onChange, disabled, testId }) => {
  const children = setting.children ?? [];

  const updateChild = (childName: string, childValue: unknown) => {
    onChange({ ...value, [childName]: childValue });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-3 tw-rounded-srf tw-bg-surface-hover" data-testid={testId}>
      {children.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-gap-3">
          {children.map((child) => (
            <ResolveControlRenderer
              key={child.name}
              setting={child}
              label={child.title}
              compact={child.control === 'number'}
              value={Object.prototype.hasOwnProperty.call(value, child.name) ? value[child.name] : defaultValueOf(child)}
              onChange={(next) => updateChild(child.name, next)}
              disabled={disabled}
              testId={`${testId}-${child.name}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResolveCompositeRow;
